import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export type Profile = {
  id: string;
  role: 'admin' | 'user';
  created_at: string;
  updated_at: string;
};

export type Farmer = {
  id: string;
  name: string;
  created_at: string;
};

export type Customer = {
  id: string;
  name: string;
  created_at: string;
};

export type Product = {
  id: string;
  entry_id: string;
  description: string;
  crates: number;
  pallets: number;
  created_at: string;
};

export type Entry = {
  id: string;
  date: string;
  delivery_note_number: string | null;
  farmer_id: string;
  customer_id: string;
  image_urls: string[] | null;
  notes: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  invoiced: boolean;
  archived: boolean;
  archived_at: string | null;
  globalgap_confirmed: 'YES' | 'NO' | null;
  products?: Product[];
  farmer?: Farmer;
  customer?: Customer;
};

export type Notification = {
  id: string;
  user_id: string;
  entry_id: string;
  type: 'created' | 'updated' | 'deleted';
  read: boolean;
  created_at: string;
};

export async function fetchEntries(startDate: string, endDate: string) {
  try {
    const { data, error } = await supabase
      .from('entries')
      .select(`
        *,
        farmer:farmers(id, name),
        customer:customers(id, name),
        products(
          id,
          description,
          crates,
          pallets
        )
      `)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching entries:', error);
    return { data: null, error };
  }
}

export async function fetchEntriesForDate(date: string) {
  try {
    const { data, error } = await supabase
      .from('entries')
      .select(`
        *,
        farmer:farmers(id, name),
        customer:customers(id, name),
        products(
          id,
          description,
          crates,
          pallets
        )
      `)
      .eq('date', date)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching entries for date:', error);
    return { data: null, error };
  }
}

export async function updateEntry(entryId: string, entryData: Partial<Entry>) {
  try {
    const { error } = await supabase
      .from('entries')
      .update({
        ...entryData,
        updated_at: new Date().toISOString()
      })
      .eq('id', entryId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error updating entry:', error);
    return { error };
  }
}

export async function createEntry(entryData: Omit<Entry, 'id' | 'created_at' | 'updated_at'>) {
  try {
    const { data, error } = await supabase
      .from('entries')
      .insert([entryData])
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('Error creating entry:', error);
    return { data: null, error };
  }
}

export async function deleteEntry(entryId: string) {
  try {
    const { error } = await supabase
      .from('entries')
      .delete()
      .eq('id', entryId);

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error deleting entry:', error);
    return { error };
  }
}